/**
 * Fund — HPH fund performance at a quarter-end: the finance workbook's Summary tab.
 *
 * WMP tenant only (the server 403s everyone else; the nav link and redirect here are
 * cosmetic). Every number is a Power BI model measure from /api/fund/summary — no fund
 * math in this file. A null renders "—" (N/A), never 0.
 */

import { useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import { KpiCard } from "../ui-components/KpiCard";
import { CalcTooltip } from "../ui-components/CalcTooltip";
import { ProgressBar } from "../ui-components/ProgressBar";
import { isDemoMode } from "../utils/PowerBIApiCall";
import { useFundSummary } from "../hooks/useQueries";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { SERIES } from "../utils/chartTheme";
import {
  formatCurrency,
  formatMillions,
  formatMultiple,
  formatPctFromFraction,
  orNA,
} from "../utils/format";
import type { FundMetrics, FundPortcoMetrics, FundSummary } from "../types/api";

const money = (v: number | null) => orNA(v, formatCurrency);
const pct = (v: number | null) => orNA(v, formatPctFromFraction);
const multiple = (v: number | null) => orNA(v, formatMultiple);

const TH =
  "text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-3";
const TD = "px-4 py-2.5 text-sm text-right tabular-nums";
const FIRM_TINT = "bg-wmp-50/60";

// =============================================================================
// Fund grid — one column per fund + Total Firm, one row per Summary line
// =============================================================================

type Cell = (f: FundMetrics, isFirm: boolean) => ReactNode;
type GridRow = { section: string } | { label: string; cell: Cell };

function StatusPill({ status }: { status: string | null }) {
  if (!status) return null;
  const tone =
    status === "Active"
      ? "bg-emerald-50 text-emerald-700"
      : status === "Closed"
        ? "bg-gray-100 text-gray-600"
        : "bg-amber-50 text-amber-700";
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}
    >
      {status}
    </span>
  );
}

/** $ value with its "% of commitment" folded underneath (two Summary rows → one). */
function ValueWithPct({
  value,
  ofCommitment,
}: {
  value: number | null;
  ofCommitment: number | null;
}) {
  return (
    <>
      <div className="text-gray-900">{money(value)}</div>
      {ofCommitment !== null && (
        <div className="text-xs text-gray-400">
          {pct(ofCommitment)} of commitment
        </div>
      )}
    </>
  );
}

/** Wraps a value in a CalcTooltip only when every input is present. */
function Explained({
  formula,
  values,
  show,
  children,
}: {
  formula: string;
  values: string;
  show: boolean;
  children: ReactNode;
}) {
  if (!show) return <>{children}</>;
  return (
    <CalcTooltip formula={formula} values={values}>
      <span className="border-b border-dotted border-gray-300 cursor-help">
        {children}
      </span>
    </CalcTooltip>
  );
}

const GRID_ROWS: GridRow[] = [
  { section: "Fund overview" },
  { label: "Vintage year", cell: (f) => f.vintageYear ?? "" },
  { label: "Fund size (commitment)", cell: (f) => money(f.fundSize) },
  {
    label: "Status at as-of date",
    cell: (f) => <StatusPill status={f.status} />,
  },

  { section: "Capital activity" },
  {
    label: "Capital called — total",
    cell: (f) => (
      <ValueWithPct value={f.calledTotal} ofCommitment={f.calledTotalPct} />
    ),
  },
  {
    label: "Capital called — investments",
    cell: (f) => (
      <ValueWithPct
        value={f.calledInvestments}
        ofCommitment={f.calledInvestmentsPct}
      />
    ),
  },
  {
    label: "Total distributions",
    cell: (f) => (
      <ValueWithPct value={f.distributions} ofCommitment={f.distributionsPct} />
    ),
  },
  {
    label: "Distributions — recallable",
    cell: (f) => (
      <ValueWithPct value={f.recallable} ofCommitment={f.recallablePct} />
    ),
  },
  {
    label: "Dry powder",
    // Firm dry powder sums Active funds only, while firm commitment / called include
    // closed and pre-inception funds — so the firm column can't show the per-fund sum.
    cell: (f, isFirm) => (
      <Explained
        show={f.dryPowder !== null}
        formula={
          isFirm
            ? "Σ over Active funds of (Commitment − Called + Recallable)"
            : "Commitment − Capital Called (Total) + Recallable"
        }
        values={
          isFirm
            ? `= ${money(f.dryPowder)} — closed and pre-inception funds have none`
            : `= ${money(f.fundSize)} − ${money(f.calledTotal)} + ${money(f.recallable)} = ${money(f.dryPowder)}`
        }
      >
        <ValueWithPct value={f.dryPowder} ofCommitment={f.dryPowderPct} />
      </Explained>
    ),
  },

  { section: "Net performance — levered" },
  { label: "Net asset value", cell: (f) => money(f.netAssetValue) },
  {
    label: "Net MOIC",
    cell: (f, isFirm) => {
      const base = isFirm ? f.calledTotal : f.paidIn;
      return (
        <Explained
          show={f.netMOIC !== null && f.distributions !== null && base !== null}
          formula={`(Distributions + NAV) ÷ ${isFirm ? "Capital Called (Total)" : "Paid-in (LP net)"}`}
          values={`= (${money(f.distributions)} + ${money(f.netAssetValue)}) ÷ ${money(base)} = ${multiple(f.netMOIC)}`}
        >
          <span className="font-semibold text-gray-900">
            {multiple(f.netMOIC)}
          </span>
        </Explained>
      );
    },
  },
  {
    label: "Net DPI",
    cell: (f, isFirm) => {
      const base = isFirm ? f.calledTotal : f.paidIn;
      return (
        <Explained
          show={f.netDPI !== null && f.distributions !== null && base !== null}
          formula={`Distributions ÷ ${isFirm ? "Capital Called (Total)" : "Paid-in (LP net)"}`}
          values={`= ${money(f.distributions)} ÷ ${money(base)} = ${multiple(f.netDPI)}`}
        >
          {multiple(f.netDPI)}
        </Explained>
      );
    },
  },
  {
    label: "Gross IRR",
    cell: (f, isFirm) => (
      <Explained
        show={f.grossIRR !== null}
        formula={
          isFirm
            ? "Pooled XIRR across funds — gross flows"
            : "XIRR of gross LP cash flows"
        }
        values={`+ gross-basis NAV at the as-of date = ${pct(f.grossIRR)}`}
      >
        {pct(f.grossIRR)}
      </Explained>
    ),
  },
  {
    label: "Net IRR",
    cell: (f, isFirm) => (
      <Explained
        show={f.netIRR !== null}
        formula={
          isFirm
            ? "Pooled XIRR across funds — net flows"
            : "XIRR of net LP cash flows"
        }
        values={`+ NAV at the as-of date = ${pct(f.netIRR)}`}
      >
        <span className="font-semibold text-gray-900">{pct(f.netIRR)}</span>
      </Explained>
    ),
  },
];

function FundGrid({
  funds,
  totalFirm,
}: {
  funds: FundMetrics[];
  totalFirm: FundMetrics;
}) {
  const columns = [
    ...funds.map((f) => ({
      f,
      isFirm: false,
      title: f.fund ?? "",
      caption: f.basis,
    })),
    {
      f: totalFirm,
      isFirm: true,
      title: "Total Firm",
      caption: "Pooled across funds",
    },
  ];
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[640px]">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={`text-left ${TH} bg-wmp-50 sticky left-0`}>USD</th>
              {columns.map((c) => (
                <th
                  key={c.title}
                  className={`text-right ${TH} ${c.isFirm ? "bg-wmp-100" : "bg-wmp-50"}`}
                >
                  <div className={c.isFirm ? "text-wmp-700" : undefined}>
                    {c.title}
                  </div>
                  {c.caption && (
                    <div className="mt-0.5 normal-case tracking-normal font-normal text-gray-400">
                      {c.caption}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GRID_ROWS.map((row) =>
              "section" in row ? (
                <tr key={row.section} className="border-t border-gray-100">
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 pt-4 pb-1.5 microlabel"
                  >
                    {row.section}
                  </td>
                </tr>
              ) : (
                <tr
                  key={row.label}
                  className="border-t border-gray-100 hover:bg-gray-50/60"
                >
                  <td className="px-4 py-2.5 text-sm text-gray-600 sticky left-0 bg-white">
                    {row.label}
                  </td>
                  {columns.map((c) => (
                    <td
                      key={c.title}
                      className={`${TD} ${c.isFirm ? FIRM_TINT : ""}`}
                    >
                      {row.cell(c.f, c.isFirm)}
                    </td>
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Portfolio companies — grouped by fund, subtotal row first (as in the workbook)
// =============================================================================

const tone = (v: number | null, floor: number) =>
  v !== null && v < floor ? "text-red-600" : "text-gray-900";

function CompanyRow({ c }: { c: FundPortcoMetrics }) {
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50/60">
      <td className="pl-8 pr-4 py-2.5 text-sm text-gray-700 sticky left-0 bg-white">
        {c.portco}
      </td>
      <td className={`${TD} text-gray-500`}>{money(c.investedCapital)}</td>
      <td className={`${TD} text-gray-900`}>{money(c.nav)}</td>
      <td className={TD}>
        {c.pctOfFundNav === null ? (
          "—"
        ) : (
          <div className="flex items-center justify-end gap-2">
            <div className="w-16 hidden sm:block">
              <ProgressBar pct={c.pctOfFundNav * 100} color={SERIES.primary} />
            </div>
            <span className="w-12">{pct(c.pctOfFundNav)}</span>
          </div>
        )}
      </td>
      <td className={`${TD} ${tone(c.grossMOIC, 1)}`}>
        {multiple(c.grossMOIC)}
      </td>
      <td className={`${TD} font-semibold ${tone(c.netMOIC, 1)}`}>
        {multiple(c.netMOIC)}
      </td>
      <td className={`${TD} ${tone(c.grossIRR, 0)}`}>{pct(c.grossIRR)}</td>
      <td className={`${TD} font-semibold ${tone(c.netIRR, 0)}`}>
        {pct(c.netIRR)}
      </td>
    </tr>
  );
}

function SubtotalRow({
  label,
  investedCapital,
  nav,
  grand = false,
}: {
  label: string;
  investedCapital: number | null;
  nav: number | null;
  grand?: boolean;
}) {
  return (
    <tr
      className={`border-t ${grand ? "border-gray-300 bg-wmp-50/60" : "border-gray-200 bg-gray-50/70"}`}
    >
      <td
        className={`px-4 py-2.5 text-sm font-semibold text-gray-900 sticky left-0 ${grand ? "bg-wmp-50" : "bg-gray-50"}`}
      >
        {label}
      </td>
      <td className={`${TD} font-semibold text-gray-700`}>
        {money(investedCapital)}
      </td>
      <td className={`${TD} font-semibold text-gray-900`}>{money(nav)}</td>
      <td colSpan={5} />
    </tr>
  );
}

function PortcoTable({ data }: { data: FundSummary }) {
  if (data.portcoGroups.length === 0) {
    return (
      <div className="card p-6 text-sm text-gray-500">
        No portfolio-company valuation breakdown at this quarter-end.
      </div>
    );
  }
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[760px]">
          <thead>
            <tr className="bg-wmp-50/70 border-b border-gray-200">
              <th className={`text-left ${TH} sticky left-0 bg-wmp-50`}>
                Fund / company
              </th>
              <th className={`text-right ${TH}`}>Invested</th>
              <th className={`text-right ${TH}`}>NAV (LP)</th>
              <th className={`text-right ${TH}`}>% of fund NAV</th>
              <th className={`text-right ${TH}`}>Gross MOIC</th>
              <th className={`text-right ${TH}`}>Net MOIC</th>
              <th className={`text-right ${TH}`}>Gross IRR</th>
              <th className={`text-right ${TH}`}>Net IRR</th>
            </tr>
          </thead>
          <tbody>
            {data.portcoGroups.map((g) => [
              <SubtotalRow
                key={g.fund}
                label={g.fund}
                investedCapital={g.investedCapital}
                nav={g.nav}
              />,
              ...g.companies.map((c) => (
                <CompanyRow key={`${g.fund}|${c.portco}`} c={c} />
              )),
            ])}
            {data.portcoTotal && (
              <SubtotalRow
                label="Total"
                investedCapital={data.portcoTotal.investedCapital}
                nav={data.portcoTotal.nav}
                grand
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Page
// =============================================================================

function Skeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-200/70" />
        ))}
      </div>
      <div className="h-96 rounded-xl bg-gray-200/70" />
      <div className="h-56 rounded-xl bg-gray-200/70" />
    </div>
  );
}

function FundContent({ enabled }: { enabled: boolean }) {
  const demo = isDemoMode();
  const [asOf, setAsOf] = useState<string | undefined>(undefined);
  const { data, error, isLoading, isPlaceholderData, refetch, isFetching } =
    useFundSummary(asOf, enabled);

  if (error) {
    return (
      <div className="w-full max-w-lg card p-6">
        <h3 className="text-gray-900 font-semibold mb-1">
          Error loading fund performance
        </h3>
        <p className="text-gray-500 text-sm">{(error as Error).message}</p>
        <div className="mt-4 flex gap-2">
          <button
            className="btn-secondary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Retrying…" : "Retry"}
          </button>
          {asOf !== undefined && (
            <button
              className="btn-secondary"
              onClick={() => setAsOf(undefined)}
            >
              Show latest quarter
            </button>
          )}
        </div>
      </div>
    );
  }

  const tf = data?.totalFirm;
  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Fund Performance
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {data?.asOfLabel ?? "Loading…"}
            {demo && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 uppercase tracking-wider">
                Demo
              </span>
            )}
          </p>
        </div>
        {data && (
          <label className="flex items-center gap-2 text-sm text-gray-500 shrink-0">
            As of
            <select
              value={asOf ?? data.asOf} // the requested quarter, even while it loads
              onChange={(e) => setAsOf(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-wmp-500 focus:outline-none"
            >
              {data.asOfDates.map((d) => (
                <option key={d.asOf} value={d.asOf}>
                  {d.quarter}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {isLoading || !data || !tf ? (
        <Skeleton />
      ) : (
        <div
          className={`space-y-8 transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}
          aria-busy={isPlaceholderData}
        >
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Net NAV"
              value={orNA(tf.netAssetValue, formatMillions)}
              footnote="Total firm, net"
            />
            <KpiCard
              label="Net MOIC"
              value={multiple(tf.netMOIC)}
              footnote="Net TVPI"
            />
            <KpiCard
              label="Net IRR"
              value={pct(tf.netIRR)}
              footnote="Pooled XIRR"
            />
            <KpiCard
              label="Dry Powder"
              value={orNA(tf.dryPowder, formatMillions)}
              footnote={tf.dryPowderLabel ?? undefined}
            />
          </section>

          <section>
            <h2 className="microlabel mb-2">Fund summary</h2>
            <FundGrid funds={data.funds} totalFirm={tf} />
          </section>

          <section>
            <h2 className="microlabel mb-2">
              Portfolio companies — LP-only, levered
            </h2>
            <PortcoTable data={data} />
          </section>

          <footer className="text-xs text-gray-400 space-y-1">
            <p>
              Each fund is reported on its own basis (shown under its name).
              Total Firm pools all funds on those bases and shows — if an active
              fund has no NAV at the as-of date.
            </p>
            <p>
              — = N/A: no NAV or valuation at that quarter-end, a fund not yet
              started, or dry powder for a closed fund. Company metrics appear
              once every invested company in the fund has a valuation that
              quarter.
            </p>
          </footer>
        </div>
      )}
    </div>
  );
}

/**
 * WMP-only gate. Cosmetic — /api/fund enforces it server-side. Redirect only when /api/me
 * said "not WMP"; if /api/me failed, load the page and let the server decide.
 */
function FundGate({ enabled }: { enabled: boolean }) {
  const { isWmp, loading, failed } = useIsAdmin();
  if (loading) return <Loading />;
  if (!isWmp && !failed) return <Navigate to="/dashboard" replace />;
  return <FundContent enabled={enabled} />;
}

function LiveFund() {
  const { inProgress } = useMsal();
  return <FundGate enabled={inProgress === InteractionStatus.None} />;
}

export function Fund() {
  if (isDemoMode()) return <FundGate enabled />;
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <LiveFund />
    </MsalAuthenticationTemplate>
  );
}
